<script setup lang="ts">
import { useHead, useSeoMeta } from '@unhead/vue'
import {
  computed,
  nextTick,
  onServerPrefetch,
  onUpdated,
  ref,
  watch,
} from 'vue'
import { useRouter } from 'vue-router'

import { useColorMode } from '@scalar/use-hooks/useColorMode'
import { customFooterClass } from '@scalar-org/css'
import { type HeadingRecord } from '@scalar-org/entities/publish'
import { ApiGuide, type GuideProps } from '@scalar-org/guide'
import {
  DocumentHeader,
  DocumentMobileHeader,
} from '@scalar-org/guide/components'
import { hydrateAll } from '@scalar-org/guide-render/elements'

import { resolveHeaderLink } from '@/helpers/resolve-link'
import { useSidebarState } from '@/hooks'

import GuideSidebarContent from './GuideSidebarContent.vue'
import { type PublishGuideProps } from './types'

const props = defineProps<PublishGuideProps>()

useSeoMeta({
  title: () => `${props.page.title} | ${props.guide.title}`,
  // TODO: Consider if we want to override the description
  // description: props.page.description ?? props.guide.description,
})
useHead({
  script: [
    {
      tagPosition: 'bodyClose',
      textContent: () => props.version.bodyScript,
    },
    {
      tagPosition: 'head',
      textContent: () => props.version.headScript,
    },
  ],
  style: [
    {
      tagPosition: 'head',
      textContent: () => props.version.footerCss,
    },
  ],
})

const { sidebarState, toggleSidebar, setSidebar, setSearch } = useSidebarState()

const html = ref<string>('')
const headings = ref<Record<string, HeadingRecord>>({})

const router = useRouter()

async function loadContent() {
  if (!props.dataFilename) return
  try {
    const content = await import(`@/assets/${props.dataFilename}.json`)
    html.value = content.default.html
    headings.value = content.default.headings
    const currentHash = window.location.hash
    if (currentHash) {
      // Wait a tick to ensure the content is rendered
      await nextTick()
      window.location.hash = '' // Clear the hash first to ensure the jump happens even if the hash is already set to this id
      window.location.hash = currentHash
    }
  } catch {
    console.error('Error loading static data', props.dataFilename)
  }
}

// Preload the content during static generation
onServerPrefetch(async () => {
  await loadContent()
})

// Components are not always unmounted on router changes so we need to watch the dataFilename
watch(
  () => props.dataFilename,
  async () => loadContent(),
  { immediate: true },
)

const guideProps = computed(
  (): GuideProps => ({
    showSidebar: sidebarState.showSidebar,
    activePage: props.page,
    sidebar: props.guide.sidebar,
    footerBelowSidebar: props.version.footerBelowSidebar,
    headings: headings.value,
    isLoaded: true,
  }),
)

const { isDarkMode } = useColorMode()

/**
 * Run hydration after the component has updated
 * Watching the page UID proved to be unreliable
 * With onUpdated we must ensure we can handle multiple calls with the same content
 */
onUpdated(async () => {
  if (import.meta.env.SSR) return
  await nextTick()
  hydrateAll({
    router,
    isDark: isDarkMode,
  })
})

function navigate(uid: string) {
  router.push({ name: uid, replace: true })
}
</script>
<template>
  <ApiGuide
    v-bind="guideProps"
    @goToNextPage="navigate"
    @goToPrevPage="navigate">
    <template #header>
      <DocumentHeader
        :activeGuide="props.guide.uid"
        :activeVersion="props.version.uid"
        :headerLinks="headerLinks"
        :project="project"
        :resolveInternalLinks="(item) => resolveHeaderLink(item, router)"
        :versions="versionNames"
        @navigate="(_, uid) => navigate(uid)"
        @selectVersion="navigate"
        @setSidebar="setSidebar" />
    </template>
    <template #mobile-header="headerProps">
      <DocumentMobileHeader
        :label="headerProps.label"
        :showSideBar="sidebarState.showSidebar"
        @showSearch="() => setSearch(true)"
        @toggleSidebar="toggleSidebar" />
    </template>
    <template #guide-sidebar>
      <GuideSidebarContent
        :activePage="page"
        :isPaid="isPaid"
        :sidebar="guide.sidebar">
      </GuideSidebarContent>
    </template>
    <template #editor-content>
      <slot name="mdx-content">
        <div
          class="active editor-content editor-static"
          v-html="html" />
      </slot>
    </template>
    <template
      v-if="version.footer"
      #footer>
      <div
        class="layout-footer"
        :class="customFooterClass"
        v-html="version.footer" />
    </template>
  </ApiGuide>
</template>
<style scoped>
.app-document {
  overflow: auto;
  width: 100dvw;
  height: 100dvh;
}
</style>
